import type {
  FlexibleScheduleMode,
  FlexibleScheduleRow
} from "./flexible-schedule-editor";

export type ScheduleAllocationState = "missing" | "under" | "met" | "over";

export interface ScheduleProductivitySummary {
  targetMinutes: number | null;
  currentMinutes: number;
  deltaMinutes: number | null;
  state: ScheduleAllocationState;
}

interface ScheduleProductivitySummaryInput {
  mode: FlexibleScheduleMode;
  rows: FlexibleScheduleRow[];
  targetMinutes?: number | null;
  dailyBaseMinutes?: number | null;
  weeklyBaseMinutes?: number | null;
}

function positiveMinutes(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : null;
}

function calculateTargetMinutes({
  mode,
  rows,
  targetMinutes,
  dailyBaseMinutes,
  weeklyBaseMinutes
}: ScheduleProductivitySummaryInput) {
  const explicitTarget = positiveMinutes(targetMinutes);
  if (explicitTarget !== null) return explicitTarget;

  if (mode === "WEEKLY_PATTERN") {
    return positiveMinutes(weeklyBaseMinutes);
  }

  const dailyTarget = positiveMinutes(dailyBaseMinutes);
  const editableWorkdayCount = rows.filter(row => !row.isDayOff).length;
  return dailyTarget !== null && editableWorkdayCount > 0
    ? dailyTarget * editableWorkdayCount
    : null;
}

export function getScheduleProductivitySummary(
  input: ScheduleProductivitySummaryInput
): ScheduleProductivitySummary {
  const targetMinutes = calculateTargetMinutes(input);
  const currentMinutes = input.rows.reduce(
    (total, row) =>
      row.isDayOff || !Number.isFinite(row.workMinutes)
        ? total
        : total + Math.max(0, Math.round(row.workMinutes)),
    0
  );

  if (targetMinutes === null) {
    return {
      targetMinutes,
      currentMinutes,
      deltaMinutes: null,
      state: "missing"
    };
  }

  const deltaMinutes = currentMinutes - targetMinutes;
  return {
    targetMinutes,
    currentMinutes,
    deltaMinutes,
    state: deltaMinutes < 0 ? "under" : deltaMinutes > 0 ? "over" : "met"
  };
}

function weekdayFromDateId(id: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(id);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).getUTCDay();
}

function weekdayFromWeeklyRow(row: FlexibleScheduleRow) {
  if (typeof row.weekday === "number" && row.weekday >= 0 && row.weekday <= 6) {
    return row.weekday;
  }

  const weekdayByKey: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
  };
  const keyedWeekday = weekdayByKey[row.id.toLowerCase()];
  if (keyedWeekday !== undefined) return keyedWeekday;

  return ["일", "월", "화", "수", "목", "금", "토"].findIndex(day =>
    row.label.startsWith(day)
  );
}

export function getSchedulePresetRowIds(
  rows: FlexibleScheduleRow[],
  mode: FlexibleScheduleMode,
  preset: "weekday" | "weekend" | "all"
) {
  if (preset === "all") return rows.map(row => row.id);

  return rows
    .filter(row => {
      const weekday =
        mode === "DAILY_DATE"
          ? row.weekday ?? weekdayFromDateId(row.id)
          : weekdayFromWeeklyRow(row);
      if (weekday === null || weekday < 0) return false;
      const isWeekend = weekday === 0 || weekday === 6;
      return preset === "weekend" ? isWeekend : !isWeekend;
    })
    .map(row => row.id);
}
